import React from "react";
import { gsap } from "gsap";

import * as S from "./styles";

const LOGO_PATH_D =
  "M36.9079 2.03326L61.9672 27.092C64.6776 29.8031 64.6776 34.1983 61.9671 36.908L36.9081 61.9671C34.1982 64.6776 29.803 64.6776 27.0918 61.9671L2.03351 36.9082C-0.677801 34.1983 -0.677801 29.803 2.03329 27.0919L27.0918 2.03333C29.8029 -0.677766 34.1983 -0.677766 36.9079 2.03326ZM24.4821 43.6953L20.2027 47.9742L30.644 58.4149L30.7291 58.4951C31.4523 59.1363 32.5438 59.1374 33.2683 58.4983L33.3569 58.4151L36.2787 55.4919L24.4821 43.6953ZM32.6258 35.5512L28.0341 40.1434L39.8306 51.9399L44.4227 47.3481L32.6258 35.5512ZM12.0589 39.8306L39.8306 12.0589L44.4228 16.6511L16.6511 44.4223L12.0589 39.8306ZM47.9744 20.2025L36.1779 31.9995L47.9744 43.796L58.4153 33.3573C59.1366 32.6354 59.1633 31.4818 58.4958 30.7288L58.4157 30.6438L47.9744 20.2025ZM36.2786 8.50688L33.3574 5.58568C32.6354 4.86407 31.4825 4.83734 30.7292 5.50532L30.6441 5.58547L5.5853 30.6442C4.86411 31.3651 4.8374 32.5183 5.50535 33.272L5.5855 33.3572L8.5069 36.2786L36.2786 8.50688Z";

export const playPreLoaderTimeline = ({
  root = "#preLoader",
  fill = "#2FBB69",
  onComplete,
  hide = false,
  drawDuration = 1,
  fillDuration = 0.5,
  fadeOutDuration = 1,
  repeat = -1,
  repeatDelay = 0,
}) => {
  if (typeof window === "undefined") return null;

  const rootEl = typeof root === "string" ? document.querySelector(root) : root;
  if (!rootEl) return null;

  const pathEl = rootEl.querySelector('[data-logo-loader-path="anim"]');
  if (!pathEl || typeof pathEl.getTotalLength !== "function") return null;

  const len = Math.ceil(pathEl.getTotalLength());

  const reset = () => {
    gsap.set(pathEl, {
      strokeDasharray: len,
      strokeDashoffset: len,
      fill: "transparent",
    });
  };

  reset();

  const tl = gsap.timeline({
    repeat,
    repeatDelay,
    onRepeat: reset,
    onComplete: () => {
      if (typeof onComplete === "function") onComplete();

      if (hide) {
        gsap.to(rootEl, {
          opacity: 0,
          duration: fadeOutDuration,
          display: "none",
        });
      }
    },
  });

  tl.to(pathEl, {
    strokeDashoffset: 0,
    duration: drawDuration,
    ease: "none",
  });

  tl.to(
    pathEl,
    {
      fill,
      duration: fillDuration,
      ease: "power1.out",
    },
    "<"
  );

  if (repeat !== 0) {
    tl.to(pathEl, {
      fill: "transparent",
      duration: fillDuration,
      ease: "power1.in",
    });
  }

  return tl;
};

/**
 * Props:
 * - id?: string
 * - backgroundColor?: string (default: "transparent")
 * - color?: string (default: "#2FBB69")
 * - size?: number (default: 64)
 * - show?: boolean (default: true)
 * - autoPlay?: boolean (default: true) — dispara a timeline automaticamente ao montar
 * - loop?: boolean (default: true) — repete a animação (quando false, roda 1 vez)
 * - fill?: string (default: color) — cor aplicada no passo de preenchimento da timeline
 * - onComplete?: () => void — callback ao finalizar (quando loop=false)
 * - hideOnComplete?: boolean (default: false) — some com fade ao finalizar (quando loop=false)
 * - drawDuration?: number (default: 2)
 * - fillDuration?: number (default: 2)
 * - fadeOutDuration?: number (default: 1)
 */
const LogoLoader = ({
  id,
  backgroundColor = "transparent",
  color = "#2FBB69",
  size = 64,
  show = true,
  autoPlay = true,
  loop = true,
  fill = color,
  onComplete,
  hideOnComplete = false,
  drawDuration = 2,
  fillDuration = 2,
  fadeOutDuration = 1,
  ...props
}) => {
  const rootRef = React.useRef(null);

  const innerScale = 0.84;
  const innerTranslate = (64 - 64 * innerScale) / 2;
  const innerTransform = `translate(${innerTranslate} ${innerTranslate}) scale(${innerScale})`;

  React.useEffect(() => {
    if (!show || !autoPlay) return;

    const tl = playPreLoaderTimeline({
      root: rootRef.current,
      fill,
      onComplete,
      hide: hideOnComplete,
      drawDuration,
      fillDuration,
      fadeOutDuration,
      repeat: loop ? -1 : 0,
    });

    return () => {
      if (tl) tl.kill();
    };
  }, [
    show,
    autoPlay,
    loop,
    fill,
    onComplete,
    hideOnComplete,
    drawDuration,
    fillDuration,
    fadeOutDuration,
  ]);

  return (
    <S.Container
      id={id}
      ref={rootRef}
      backgroundColor={backgroundColor}
      size={size}
      show={show}
      {...props}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g transform={innerTransform}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d={LOGO_PATH_D}
            fill={color}
            fillOpacity={0.16}
          />

          <path
            data-logo-loader-path="anim"
            fillRule="evenodd"
            clipRule="evenodd"
            d={LOGO_PATH_D}
            fill="transparent"
            stroke={color}
            strokeWidth="2.9"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </g>
      </svg>
    </S.Container>
  );
};

export default LogoLoader;